// File: src/components/PasswordResetView.js
// Forgotten passwords: asking for a link, and using one.
//
// Both are public forms, so both carry a Turnstile check. The request form
// deliberately reports the same thing whether or not the address has an
// account - anything else turns it into a way to discover who is registered.

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Mail } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { apiGet, apiPost } from '../services/api';
import TurnstileWidget from './TurnstileWidget';

const MIN_PASSWORD_LENGTH = 8;

function Shell({ title, children }) {
  const { t } = useLanguage();

  return (
    <div className="max-w-md mx-auto">
      {/* Explicitly /login: "/" is the calendar now, not the sign-in page. */}
      <Link to="/login" className="inline-flex items-center gap-2 text-ink-muted hover:text-ink mb-6">
        <ArrowLeft className="w-4 h-4" />
        {t('backToLogin')}
      </Link>
      <div className="bg-paper border border-line rounded-lg p-6">
        <h1 className="text-2xl font-bold text-ink mb-4">{title}</h1>
        {children}
      </div>
    </div>
  );
}

// Step one: ask for the link.
export function ForgotPasswordView() {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [resetSignal, setResetSignal] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setSending(true);
    setError('');
    try {
      await apiPost('/auth/forgot-password',
        { email, 'cf-turnstile-response': turnstileToken });
      // Shown whatever the answer was: the server does not say whether the
      // address is known, and neither does this.
      setSent(true);
    } catch (err) {
      setError((err.body && err.body.message) || err.message);
      setTurnstileToken('');
      setResetSignal(n => n + 1);
    } finally {
      setSending(false);
    }
  };

  return (
    <Shell title={t('forgotPasswordTitle')}>
      {sent ? (
        <div className="bg-green-50 dark:bg-green-500/20 border border-green-500 text-green-800 dark:text-green-200 px-4 py-3 rounded flex items-start gap-2">
          <Mail className="w-5 h-5 flex-shrink-0 mt-0.5" />
          {t('forgotPasswordSent')}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-ink-muted">{t('forgotPasswordHelp')}</p>

          <div>
            <label className="block text-ink-muted mb-2 font-semibold">{t('entryEmail')}</label>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
            />
          </div>

          <TurnstileWidget onToken={setTurnstileToken} resetSignal={resetSignal} />

          {error && (
            <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <button
            onClick={submit}
            disabled={sending || !email || !turnstileToken}
            className="w-full bg-accent hover:opacity-90 disabled:bg-paper-alt disabled:text-ink-muted text-accent-ink py-3 rounded font-semibold"
          >
            {t('forgotPasswordSend')}
          </button>

          {/* Staff accounts are deliberately out of scope here. */}
          <p className="text-ink-muted text-sm">{t('forgotPasswordCompetitorsOnly')}</p>
        </div>
      )}
    </Shell>
  );
}

// Step two: use it.
export function ResetPasswordView() {
  const { t } = useLanguage();
  const { resetToken } = useParams();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [resetSignal, setResetSignal] = useState(0);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // Checked up front, so nobody types a new password into a dead link.
  useEffect(() => {
    let cancelled = false;
    apiGet(`/auth/reset-password/${resetToken}`)
      .then(result => { if (!cancelled) setValid(result.valid); })
      .catch(() => { if (!cancelled) setValid(false); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [resetToken]);

  const submit = async () => {
    if (password !== confirm) {
      setError(t('resetPasswordMismatch'));
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t('entryPasswordHint'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      await apiPost('/auth/reset-password',
        { token: resetToken, password, 'cf-turnstile-response': turnstileToken });
      setDone(true);
    } catch (err) {
      setError((err.body && err.body.message) || err.message);
      setTurnstileToken('');
      setResetSignal(n => n + 1);
    } finally {
      setSaving(false);
    }
  };

  if (checking) {
    return <Shell title={t('resetPasswordTitle')}>
      <p className="text-ink-muted">{t('resetPasswordChecking')}</p>
    </Shell>;
  }

  if (done) {
    return <Shell title={t('resetPasswordTitle')}>
      <div className="bg-green-50 dark:bg-green-500/20 border border-green-500 text-green-800 dark:text-green-200 px-4 py-3 rounded flex items-start gap-2">
        <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
        {t('resetPasswordDone')}
      </div>
    </Shell>;
  }

  if (!valid) {
    return <Shell title={t('resetPasswordTitle')}>
      <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded">
        {t('resetPasswordExpired')}
      </div>
    </Shell>;
  }

  return (
    <Shell title={t('resetPasswordTitle')}>
      <div className="space-y-4">
        <div>
          <label className="block text-ink-muted mb-2 font-semibold">{t('resetPasswordNew')}</label>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
          />
          <p className="text-ink-muted text-xs mt-1">{t('entryPasswordHint')}</p>
        </div>

        <div>
          <label className="block text-ink-muted mb-2 font-semibold">{t('resetPasswordConfirm')}</label>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
          />
        </div>

        <TurnstileWidget onToken={setTurnstileToken} resetSignal={resetSignal} />

        {error && (
          <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <button
          onClick={submit}
          disabled={saving || !password || !turnstileToken}
          className="w-full bg-accent hover:opacity-90 disabled:bg-paper-alt disabled:text-ink-muted text-accent-ink py-3 rounded font-semibold"
        >
          {t('resetPasswordSubmit')}
        </button>
      </div>
    </Shell>
  );
}
